import { useEffect, useRef, useState } from 'react';
import type {
  AuftragAnsicht,
  PositionEingabe,
  Rapport,
  TaetigkeitsartAnsicht,
} from '@arbeitsrapporte/shared';
import { api } from '../api';
import { Fehler } from '../bausteine/Meldung';
import { Positionen, type Positionszeile } from '../bausteine/Positionen';
import { navigiere, setzeWaechter } from '../router';

/** Der Wert, der in der Auswahl „anderer Name …" steht – kein Name kann so heißen. */
const FREITEXT = '\u0000freitext';

/** Die Eingabe ohne das, was nur zum Anzeigen festgehalten war. */
function eingabeAus(z: Positionszeile): PositionEingabe {
  return {
    art: z.art,
    bezeichnung: z.bezeichnung,
    menge: z.menge,
    einheit: z.einheit,
    von: z.von,
    bis: z.bis,
    artikelId: z.artikelId,
    taetigkeitsartId: z.taetigkeitsartId,
    auftragspositionNummer: z.auftragspositionNummer,
    verrechenbar: z.verrechenbar,
    bemerkung: z.bemerkung,
  };
}

export function Bearbeiten({ id }: { id: string }) {
  const [r, setR] = useState<Rapport | null>(null);
  const [auftrag, setAuftrag] = useState<AuftragAnsicht | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const [datum, setDatum] = useState('');
  const [einsatzort, setEinsatzort] = useState('');
  const [ansprechpartner, setAnsprechpartner] = useState('');
  const [taetigkeit, setTaetigkeit] = useState('');
  const [bemerkung, setBemerkung] = useState('');
  const [positionen, setPositionen] = useState<Positionszeile[]>([]);
  const [taetigkeitsarten, setTaetigkeitsarten] = useState<TaetigkeitsartAnsicht[]>([]);
  const [partner, setPartner] = useState<{ id: string; name: string; funktion: string }[]>([]);
  /** Von Hand getippt statt aus der Liste – wer am Werktor steht, steht in keinem ERP. */
  const [freitext, setFreitext] = useState(false);

  /**
   * Was geladen wurde, als Vergleich: Verlassen ohne Speichern fragt nur,
   * wenn sich etwas geändert hat. Ein Formular, das bei jedem „Abbrechen"
   * nachfragt, wird weggeklickt, ohne zu lesen.
   */
  const geladenerStand = useRef<string | null>(null);
  const stand = JSON.stringify({
    datum,
    einsatzort,
    ansprechpartner,
    taetigkeit,
    bemerkung,
    positionen,
  });
  const geaendert = geladenerStand.current !== null && stand !== geladenerStand.current;

  useEffect(() => {
    api
      .rapport(id)
      .then((geladen) => {
        setR(geladen);
        setDatum(geladen.datum);
        setEinsatzort(geladen.einsatzort);
        setAnsprechpartner(geladen.ansprechpartner);
        setTaetigkeit(geladen.taetigkeit);
        setBemerkung(geladen.bemerkung);
        setPositionen(geladen.positionen);
        geladenerStand.current = JSON.stringify({
          datum: geladen.datum,
          einsatzort: geladen.einsatzort,
          ansprechpartner: geladen.ansprechpartner,
          taetigkeit: geladen.taetigkeit,
          bemerkung: geladen.bemerkung,
          positionen: geladen.positionen,
        });
        api
          .auftrag(geladen.auftragId)
          .then(setAuftrag)
          .catch(() => setAuftrag(null));
        /*
         * Nur die der Gesellschaft dieses Rapports: Die Artikelnummern von
         * CH und DE sind verschieden, und eine Liste mit beiden lüde zum
         * Danebengreifen ein.
         */
        api
          .taetigkeitsarten(geladen.gesellschaft)
          .then(setTaetigkeitsarten)
          .catch(() => setTaetigkeitsarten([]));
        /*
         * Die Ansprechpartner des Kunden aus dem Spiegel – Regel 14: Wer
         * gemeint ist, steht im ERP und wird nicht abgetippt. Freitext
         * bleibt daneben möglich; nicht jeder, der am Werktor steht, ist
         * dort gepflegt.
         */
        if (geladen.kundeId) {
          api
            .ansprechpartner(geladen.kundeId)
            .then(setPartner)
            .catch(() => setPartner([]));
        }
      })
      .catch(setFehler);
  }, [id]);

  /*
   * Der Wächter vor dem Verlassen: Beim Kunden, auf dem Telefon, ist ein
   * Anruf oder ein Griff auf „Meine" schnell passiert – und zwanzig Felder
   * wären weg. Verweise und `navigiere` fragen über den Router, das
   * Neuladen und Schließen des Tabs über `beforeunload`. Beim Speichern
   * wird der Wächter vorher abgehängt.
   */
  useEffect(() => {
    if (!geaendert) {
      setzeWaechter(null);

      return;
    }

    const frage = 'Ungespeicherte Änderungen verwerfen?';

    setzeWaechter(() => window.confirm(frage));

    const beiVerlassen = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', beiVerlassen);

    return () => {
      setzeWaechter(null);
      window.removeEventListener('beforeunload', beiVerlassen);
    };
  }, [geaendert]);

  if (fehler && !r) return <Fehler fehler={fehler} />;
  if (!r) return <p className="text-slate-600">Lädt …</p>;

  if (!r.erlaubt.aendern || r.unterschrift) {
    return (
      <>
        <p className="mb-4 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900">
          Dieser Rapport lässt sich gerade nicht ändern.
        </p>
        <a
          href={`/rapport/${r.id}`}
          className="text-slate-700 underline underline-offset-4 hover:text-slate-900"
        >
          Zurück zum Rapport
        </a>
      </>
    );
  }

  /*
   * Die Liste zeigt sich, solange der Name aus ihr stammt – oder noch
   * keiner dasteht. Ein Name, den das ERP nicht kennt (ein alter Rapport,
   * ein Freitext), darf nicht stillschweigend verschwinden, weil er in
   * keinem `<option>` steht.
   */
  const ausListe =
    partner.length > 0 &&
    !freitext &&
    (ansprechpartner === '' || partner.some((p) => p.name === ansprechpartner));

  const speichern = async (danach: 'rapport' | 'unterschrift') => {
    setLaeuft(true);
    setFehler(null);

    try {
      await api.aendere(r.id, {
        datum,
        einsatzort,
        ansprechpartner,
        taetigkeit,
        bemerkung,
        positionen: positionen.map(eingabeAus),
      });
      /* Gespeichert – der Wächter hat nichts mehr zu bewachen. */
      geladenerStand.current = stand;
      navigiere(danach === 'unterschrift' ? `/rapport/${r.id}/unterschrift` : `/rapport/${r.id}`, {
        ohneWaechter: true,
      });
    } catch (f) {
      setFehler(f);
      setLaeuft(false);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void speichern('rapport');
      }}
    >
      <h1 className="text-2xl font-semibold">{r.nummer}</h1>
      <p className="mb-4 text-slate-600">
        {r.auftragNummer} {r.auftragBezeichnung} · {r.kundeName || 'kein Kunde'}
      </p>

      <Fehler fehler={fehler} />

      {/* Was die Leitung beim Zurückgeben gesagt hat – hier, wo es behoben wird. */}
      {r.zurueckgabeGrund && (
        <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
          <span className="font-medium">Zurückgegeben:</span> {r.zurueckgabeGrund}
        </p>
      )}

      <div className="karte space-y-4">
        <div>
          <label className="feldname" htmlFor="datum">
            Datum
          </label>
          <input
            id="datum"
            type="date"
            required
            className="feld"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
          />
        </div>

        <div>
          <label className="feldname" htmlFor="einsatzort">
            Einsatzort
          </label>
          <input
            id="einsatzort"
            className="feld"
            value={einsatzort}
            onChange={(e) => setEinsatzort(e.target.value)}
            placeholder={auftrag?.kundeAnschrift || 'Werk, Anlage, Adresse'}
          />
          {auftrag?.kundeAnschrift && einsatzort.trim() !== auftrag.kundeAnschrift && (
            <button
              type="button"
              className="mt-1 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
              onClick={() => setEinsatzort(auftrag.kundeAnschrift)}
            >
              Adresse des Kunden einsetzen: {auftrag.kundeAnschrift}
            </button>
          )}
        </div>

        <div>
          <label className="feldname" htmlFor="ansprechpartner">
            Ansprechpartner beim Kunden
          </label>
          {ausListe ? (
            <select
              id="ansprechpartner"
              className="feld"
              value={ansprechpartner}
              onChange={(e) => {
                if (e.target.value === FREITEXT) {
                  setAnsprechpartner('');
                  setFreitext(true);
                } else {
                  setAnsprechpartner(e.target.value);
                }
              }}
            >
              <option value="">– niemand angegeben –</option>
              {partner.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                  {p.funktion ? ` (${p.funktion})` : ''}
                </option>
              ))}
              <option value={FREITEXT}>anderer Name …</option>
            </select>
          ) : (
            <input
              id="ansprechpartner"
              className="feld"
              value={ansprechpartner}
              onChange={(e) => setAnsprechpartner(e.target.value)}
              placeholder="Name"
            />
          )}
          {partner.length > 0 && !ausListe && (
            <button
              type="button"
              className="mt-1 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
              onClick={() => {
                setAnsprechpartner('');
                setFreitext(false);
              }}
            >
              Aus dem ERP wählen
            </button>
          )}
        </div>

        <div>
          <label className="feldname" htmlFor="taetigkeit">
            Ausgeführte Arbeiten
          </label>
          <textarea
            id="taetigkeit"
            rows={5}
            className="feld"
            value={taetigkeit}
            onChange={(e) => setTaetigkeit(e.target.value)}
          />
        </div>
      </div>

      <Positionen
        zeilen={positionen}
        auftrag={auftrag}
        taetigkeitsarten={taetigkeitsarten}
        onChange={setPositionen}
      />

      <div className="karte">
        <label className="feldname" htmlFor="bemerkung">
          Interne Bemerkung (nicht auf dem Rapport)
        </label>
        <textarea
          id="bemerkung"
          rows={2}
          className="feld"
          value={bemerkung}
          onChange={(e) => setBemerkung(e.target.value)}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button type="submit" className="knopf-haupt" disabled={laeuft}>
          Speichern
        </button>
        <button
          type="button"
          className="knopf"
          disabled={laeuft || positionen.length === 0}
          title={
            positionen.length === 0 ? 'Ohne Position gibt es nichts zu unterschreiben.' : undefined
          }
          onClick={() => speichern('unterschrift')}
        >
          Speichern und unterschreiben lassen
        </button>
        <a
          href={`/rapport/${r.id}`}
          className="px-2 py-1 text-sm text-slate-500 underline underline-offset-4 hover:text-slate-900"
        >
          Abbrechen
        </a>
      </div>
    </form>
  );
}
